import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../App";
import { 
  Send, 
  BookOpen, 
  CheckCircle, 
  Clock, 
  XCircle,
  Plus,
  ChevronLeft,
  Sparkles,
  Gift
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Contribute() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [myContributions, setMyContributions] = useState([]);
  const [showForm, setShowForm] = useState(true);
  
  const [formData, setFormData] = useState({
    baoulé: "",
    français: "",
    phonétique: "",
    catégorie: "général",
    exemple_baoulé: "",
    exemple_français: ""
  });

  const categories = [
    "salutations", "nombres", "famille", "nourriture", 
    "nature", "verbes", "expressions", "proverbes", "général"
  ];

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    fetchMyContributions();
  }, [user, navigate]);

  const fetchMyContributions = async () => {
    try {
      const res = await fetch(`${API}/api/contributions/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMyContributions(data);
      }
    } catch (err) {
      console.error("Error fetching contributions:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const res = await fetch(`${API}/api/contributions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setSuccess(true);
        setFormData({
          baoulé: "",
          français: "",
          phonétique: "",
          catégorie: "général",
          exemple_baoulé: "",
          exemple_français: ""
        });
        fetchMyContributions();
        setTimeout(() => setSuccess(false), 5000);
      } else {
        const data = await res.json();
        setError(data.detail || "Erreur lors de la soumission");
      }
    } catch (err) {
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return (
          <span className="flex items-center gap-1 text-green-600 bg-green-100 px-2 py-1 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" /> Approuvé
          </span>
        );
      case "rejected":
        return (
          <span className="flex items-center gap-1 text-red-600 bg-red-100 px-2 py-1 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" /> Rejeté
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-amber-600 bg-amber-100 px-2 py-1 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" /> En attente
          </span>
        );
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="contribute-page">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Retour
          </button>
          <h1 className="font-heading text-xl font-bold">Contribuer</h1>
          <div className="w-20"></div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-heading text-3xl font-bold mb-2">
            Enrichissez le dictionnaire
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            Partagez vos connaissances en baoulé et aidez à préserver cette belle langue. 
            Chaque contribution approuvée vous rapporte <span className="text-primary font-semibold">+20 points</span> !
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={showForm ? "default" : "outline"}
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nouvelle contribution
          </Button>
          <Button
            variant={!showForm ? "default" : "outline"}
            onClick={() => setShowForm(false)}
            className="flex items-center gap-2"
          >
            <BookOpen className="w-4 h-4" />
            Mes contributions ({myContributions.length})
          </Button>
        </div>

        {showForm ? (
          /* Contribution Form */
          <div className="bg-card rounded-2xl border border-border p-6 shadow-sm">
            <h3 className="font-heading text-xl font-bold mb-6 flex items-center gap-2">
              <Gift className="w-5 h-5 text-primary" />
              Proposer un nouveau mot
            </h3>

            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3" data-testid="success-message">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <div>
                  <p className="font-semibold text-green-800">Contribution envoyée !</p>
                  <p className="text-sm text-green-600">Votre proposition sera examinée par notre équipe. +5 points gagnés !</p>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3" data-testid="error-message">
                <XCircle className="w-5 h-5 text-red-600" />
                <p className="text-red-800">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                {/* Baoulé Word */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Mot en Baoulé <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.baoulé}
                    onChange={(e) => setFormData({...formData, baoulé: e.target.value})}
                    placeholder="Ex: Akwaba"
                    required
                    data-testid="input-baoule"
                    className="h-12"
                  />
                </div>

                {/* French Translation */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Traduction en Français <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.français}
                    onChange={(e) => setFormData({...formData, français: e.target.value})}
                    placeholder="Ex: Bienvenue"
                    required
                    data-testid="input-francais"
                    className="h-12"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                {/* Phonétique */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Prononciation (phonétique)
                  </label>
                  <Input
                    type="text"
                    value={formData.phonétique}
                    onChange={(e) => setFormData({...formData, phonétique: e.target.value})}
                    placeholder="Ex: /a-kwa-ba/"
                    data-testid="input-phonetique"
                    className="h-12"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Catégorie
                  </label>
                  <select
                    value={formData.catégorie}
                    onChange={(e) => setFormData({...formData, catégorie: e.target.value})}
                    className="w-full h-12 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="select-categorie"
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Example in Baoulé */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Exemple d'utilisation (en baoulé)
                </label>
                <Input
                  type="text"
                  value={formData.exemple_baoulé}
                  onChange={(e) => setFormData({...formData, exemple_baoulé: e.target.value})}
                  placeholder="Ex: Akwaba, a wuli yé?"
                  data-testid="input-exemple-baoule"
                  className="h-12"
                />
              </div>

              {/* Example in French */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Traduction de l'exemple
                </label>
                <Input
                  type="text"
                  value={formData.exemple_français}
                  onChange={(e) => setFormData({...formData, exemple_français: e.target.value})}
                  placeholder="Ex: Bienvenue, comment vas-tu?"
                  data-testid="input-exemple-francais"
                  className="h-12"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || !formData.baoulé || !formData.français}
                className="w-full h-12 text-base font-semibold"
                data-testid="submit-contribution"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Envoi en cours...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    Soumettre ma contribution
                  </span>
                )}
              </Button>
            </form>
          </div>
        ) : (
          /* My Contributions List */
          <div className="space-y-4">
            {myContributions.length === 0 ? (
              <div className="bg-card rounded-2xl border border-border p-8 text-center">
                <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading text-lg font-bold mb-2">Aucune contribution</h3>
                <p className="text-muted-foreground mb-4">
                  Vous n'avez pas encore soumis de mot. Commencez maintenant !
                </p>
                <Button onClick={() => setShowForm(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Faire une contribution
                </Button>
              </div>
            ) : (
              myContributions.map((contrib) => (
                <div 
                  key={contrib.contribution_id}
                  className="bg-card rounded-xl border border-border p-5 hover:shadow-md transition-shadow"
                  data-testid={`contribution-${contrib.contribution_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-heading text-xl font-bold text-primary">
                        {contrib.baoulé}
                      </h4>
                      {contrib.phonétique && (
                        <p className="text-sm text-muted-foreground">{contrib.phonétique}</p>
                      )}
                    </div>
                    {getStatusBadge(contrib.status)}
                  </div>
                  
                  <p className="text-lg mb-2">{contrib.français}</p>
                  
                  {contrib.exemple_baoulé && (
                    <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm italic text-foreground">"{contrib.exemple_baoulé}"</p>
                      {contrib.exemple_français && (
                        <p className="text-sm text-muted-foreground mt-1">→ {contrib.exemple_français}</p>
                      )}
                    </div>
                  )}
                  
                  <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="px-2 py-1 bg-muted rounded">{contrib.catégorie}</span>
                    <span>{new Date(contrib.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 p-6 bg-primary/5 border border-primary/20 rounded-2xl">
          <h4 className="font-heading font-bold mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Comment ça marche ?
          </h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">1.</span>
              Soumettez un mot baoulé avec sa traduction française
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">2.</span>
              Notre équipe vérifie l'exactitude de votre contribution
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">3.</span>
              Si approuvée, le mot est ajouté au dictionnaire et vous gagnez <span className="font-semibold text-primary">+20 points</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary font-bold">4.</span>
              Vous recevez <span className="font-semibold text-primary">+5 points</span> juste pour avoir soumis une contribution
            </li>
          </ul>
        </div>
      </main>
    </div>
  );
}
